'use client';

import type { SearchProductService } from '@musinsa/services-curator';
import type { HTMLAttributes, ReactNode } from 'react';
import { ProductInformationPrimitive } from '../../../common';
import type { BaseProductImageItem } from './BaseProductImageItem';
import { ProductShell } from './ProductShell';

/**
 * SearchedProduct가 실제로 읽는 필드의 구조적 최소 인터페이스.
 * SearchProductService / LikedProductService / 큐레이션 폼 아이템 모두 만족한다.
 */
export type SearchedProductItem = BaseProductImageItem & {
  brandName: string;
  brandNo?: number | null;
  price: number;
  salesRate?: number | null;
  heartCount?: number;
  reviewScore?: string;
  reviewCount?: number;
  expectedEarnings?: number;
  webUrl?: string;
  // RADAR 가격/라벨 필드 (서버 응답에서 유래, 없을 수 있음)
  finalPrice?: number | null;
  finalDiscount?: number | null;
  isExclusive?: boolean | null;
  isLowestPrice?: boolean | null;
  existsCoupon?: boolean | null;
};

interface SearchedProductProps<T extends SearchedProductItem = SearchProductService>
  extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  item: T;
  checked?: boolean;
  index?: number;
  children?: ReactNode;
  imageClassName?: string;
  informationClassName?: string;
  informmationChildren?: ReactNode;
  onClickCheckbox?: () => void;
  onClickCloseButton?: () => void;
  onClickLikeButton?: (item: T, nextIsLiked: boolean) => void;
  /** 29cm 딥링크 활성화 여부 */
  enableDeeplink?: boolean;
  callbackClick?: (item: T) => void;
  callbackImpression?: (item: T) => void;
}

export const SearchedProduct = <T extends SearchedProductItem = SearchProductService>({
  className,
  item,
  checked,
  index,
  children,
  imageClassName,
  informationClassName,
  informmationChildren,
  onClickCheckbox,
  onClickCloseButton,
  onClickLikeButton,
  enableDeeplink = false,
  callbackClick,
  callbackImpression,
  ...rest
}: SearchedProductProps<T>) => {
  const finalPrice = item.finalPrice ?? item.price;
  const finalDiscount = item.finalDiscount ?? item.salesRate ?? 0;
  const isExclusive = item.isExclusive === true;
  // 단독 라벨이 있으면 최저가도전 미노출 (Figma 정책)
  const isLowestPrice = item.isLowestPrice === true && !isExclusive;
  const existsCoupon = item.existsCoupon === true;

  const infoBlock = (
    <ProductInformationPrimitive.Container className={informationClassName}>
      <div>
        <ProductInformationPrimitive.BrandName brandName={item.brandName} />
        <ProductInformationPrimitive.GoodsName goodsName={item.goodsName} />
      </div>

      <div className="flex flex-col">
        <ProductInformationPrimitive.DiscountPriceRow
          finalPrice={finalPrice}
          finalDiscount={finalDiscount}
        />

        <ProductInformationPrimitive.EngagementRow>
          <ProductInformationPrimitive.HeartLikeCount likeCount={item.heartCount ?? 0} />
          {item.reviewScore && (
            <ProductInformationPrimitive.ReviewScore reviewScore={item.reviewScore} />
          )}
          <ProductInformationPrimitive.ReviewCount reviewCount={item.reviewCount ?? 0} />
        </ProductInformationPrimitive.EngagementRow>

        {(existsCoupon || isExclusive || isLowestPrice) && (
          <ProductInformationPrimitive.ProductBadgeContainer>
            {existsCoupon && <ProductInformationPrimitive.CouponBadge />}
            {isExclusive && <ProductInformationPrimitive.ExclusiveGoodsBadge />}
            {isLowestPrice && <ProductInformationPrimitive.LowestPriceBadge />}
          </ProductInformationPrimitive.ProductBadgeContainer>
        )}

        {item.expectedEarnings != null && (
          <ProductInformationPrimitive.ExpectedEarnings expectedEarnings={item.expectedEarnings} />
        )}
      </div>

      {informmationChildren && (
        <ProductInformationPrimitive.Footer>
          {informmationChildren}
        </ProductInformationPrimitive.Footer>
      )}
    </ProductInformationPrimitive.Container>
  );

  return (
    <ProductShell
      className={className}
      item={item}
      checked={checked}
      index={index}
      imageClassName={imageClassName}
      infoBlock={infoBlock}
      onClickCheckbox={onClickCheckbox}
      onClickCloseButton={onClickCloseButton}
      onClickLikeButton={onClickLikeButton}
      enableDeeplink={enableDeeplink}
      productUrl={item.webUrl}
      callbackClick={callbackClick}
      callbackImpression={callbackImpression}
      {...rest}
    >
      {children}
    </ProductShell>
  );
};
